import { Link } from "react-router-dom";
import {
  ArrowRight,
  TrendingDown,
  Zap,
  TrendingUp,
  Cog,
  Quote,
  GraduationCap,
} from "lucide-react";
import { site } from "../content/site";

const ICONS = { TrendingDown, Zap, TrendingUp, Cog };

const About = () => {
  const a = site.about;

  return (
    <>
      {/* HERO */}
      <section
        className="relative min-h-[100vh] flex items-center pt-28 pb-20 border-b border-white/10 overflow-hidden"
        data-testid="about-hero"
      >
        <div className="absolute inset-0 grain z-10 opacity-40" />

        <div className="relative z-20 max-w-7xl mx-auto px-6 sm:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Brand image — left */}
            <div className="lg:col-span-6 reveal order-1">
              <img
                src={a.brandImage}
                alt="HawkEdge AI Advisory brand"
                className="block w-full h-auto"
              />
            </div>

            {/* Hero text — right */}
            <div className="lg:col-span-6 order-2">
              <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/60 mb-6">
                {a.eyebrow}
              </p>
              <h1 className="font-serif-display text-[2.6rem] sm:text-6xl lg:text-[4.5rem] text-white leading-[1.0] tracking-tight">
                {a.title}
              </h1>
              <p className="mt-8 max-w-2xl text-base sm:text-lg font-sans-modern font-light text-white/80 leading-relaxed">
                {a.subtitle}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to={a.ctaPrimary.href}
                  data-testid="about-cta-primary"
                  className="group inline-flex items-center justify-center h-12 px-8 bg-white text-[#711457] font-sans-modern font-semibold text-sm tracking-wide hover:bg-white/90 transition-colors"
                >
                  {a.ctaPrimary.label}
                  <ArrowRight
                    size={16}
                    className="ml-2 group-hover:translate-x-1 transition-transform"
                  />
                </Link>
                <Link
                  to={a.ctaSecondary.href}
                  data-testid="about-cta-secondary"
                  className="inline-flex items-center justify-center h-12 px-8 border border-white/25 text-white font-sans-modern font-semibold text-sm tracking-wide hover:bg-white/5 hover:border-white/50 transition-colors"
                >
                  {a.ctaSecondary.label}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER SPOTLIGHT */}
      <section
        className="py-24 sm:py-32 border-t border-white/10"
        data-testid="about-founder"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Founder photo card */}
          <div className="lg:col-span-5 reveal">
            <div className="relative aspect-[4/5] bg-[#5C0E45] border border-white/15 overflow-hidden group">
              <img
                src={a.founder.photo}
                alt={a.founder.name}
                className="absolute inset-0 w-full h-full object-cover object-[center_25%] transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#711457] via-[#711457]/35 to-[#711457]/0" />
              <div className="absolute top-0 left-0 right-0 p-6 sm:p-8 flex items-start justify-between">
                <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/85">
                  {a.founder.eyebrow}
                </p>
                <span
                  aria-hidden="true"
                  className="block w-2.5 h-2.5 bg-white/70 rotate-45"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                <p className="font-serif-display text-2xl sm:text-3xl text-white leading-tight">
                  {a.founder.name}
                </p>
                <p className="mt-2 text-sm font-sans-modern text-white/85">
                  {a.founder.role}
                </p>
                <p className="mt-1 text-[11px] font-sans-modern uppercase tracking-[0.2em] text-white/70">
                  {a.founder.discipline}
                </p>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="lg:col-span-7 reveal">
            <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/50 mb-6">
              The founder
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05] tracking-tight">
              A practitioner's depth.<br />A strategist's creativity.
            </h2>
            <div className="mt-8 space-y-6">
              {a.founder.bio.map((p, i) => (
                <p
                  key={i}
                  className="text-base sm:text-lg font-sans-modern font-light text-white/75 leading-relaxed"
                >
                  {p}
                </p>
              ))}
            </div>

            {/* Industries served chips */}
            <div className="mt-10 pt-8 border-t border-white/10">
              <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/50 mb-5">
                {a.industriesServedEyebrow}
              </p>
              <div className="flex flex-wrap gap-2">
                {a.industriesServed.map((ind) => (
                  <span
                    key={ind}
                    data-testid={`industry-chip-${ind.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-flex items-center h-9 px-4 border border-white/20 text-sm font-sans-modern text-white/85 hover:border-white/40 hover:bg-white/5 transition-colors"
                  >
                    {ind}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section
        className="py-20 sm:py-28 border-t border-white/10 bg-[#5C0E45]/50"
        data-testid="about-roles"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/50">
              {a.rolesEyebrow}
            </p>
          </div>
          <div className="lg:col-span-9">
            <p className="font-serif-display italic text-xl sm:text-2xl lg:text-[1.7rem] text-white/95 leading-[1.5] tracking-tight max-w-3xl">
              {a.rolesParagraph}
            </p>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section
        className="relative py-24 sm:py-36 border-t border-white/10 overflow-hidden"
        data-testid="about-quote"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-8 reveal">
          <Quote
            size={48}
            className="text-white/25 mb-8"
            strokeWidth={1.25}
          />
          <blockquote className="font-serif-display italic text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.2] tracking-tight">
            {a.quote}
          </blockquote>
          <div className="mt-10 flex items-center gap-4">
            <span className="block w-12 h-px bg-white/40" />
            <p className="text-sm font-sans-modern text-white/65 tracking-wide">
              {a.quoteAttribution}
            </p>
          </div>
        </div>
      </section>

      {/* IMPACT STATS */}
      <section
        className="py-20 sm:py-28 border-t border-white/10"
        data-testid="about-stats"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/50 mb-12">
            {a.statsEyebrow}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-white/10">
            {a.stats.map((s, i) => (
              <div
                key={s.value}
                data-testid={`stat-${i}`}
                className={`py-12 px-2 ${i !== 0 ? "lg:border-l border-white/10" : ""} ${i % 2 !== 0 ? "border-l border-white/10 lg:border-l" : ""} ${i >= 2 ? "border-t lg:border-t-0 border-white/10" : ""}`}
              >
                <p className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-none">
                  {s.value}
                </p>
                <p className="mt-5 text-xs sm:text-sm font-sans-modern font-light text-white/65 leading-snug max-w-[200px]">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATOR — Intent-Based AI */}
      <section
        className="relative py-28 sm:py-36 border-t border-white/10 overflow-hidden"
        data-testid="about-differentiator"
      >
        <img
          src={site.images.philosophy}
          alt="Strategic chess piece"
          className="absolute inset-0 w-full h-full object-cover opacity-15 grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#711457] via-[#711457]/85 to-[#711457]/40 z-10" />

        <div className="relative z-20 max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 reveal">
            <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/55 mb-6">
              {a.differentiator.eyebrow}
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
              {a.differentiator.title}
            </h2>
            <p className="mt-8 text-base sm:text-lg font-sans-modern font-light text-white/80 leading-relaxed max-w-2xl">
              {a.differentiator.body}
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE DELIVER */}
      <section
        className="py-24 sm:py-32 border-t border-white/10 bg-[#5C0E45]/40"
        data-testid="about-deliverables"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="max-w-3xl">
            <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/55 mb-6">
              {a.deliverablesEyebrow}
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05] tracking-tight">
              {a.deliverablesTitle}
            </h2>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {a.deliverables.map((d, i) => {
              const Icon = ICONS[d.icon];
              return (
                <div
                  key={d.title}
                  data-testid={`deliverable-${i}`}
                  className="bg-[#711457] p-8 sm:p-10 min-h-[220px] flex flex-col justify-between"
                >
                  <div className="w-12 h-12 border border-white/25 flex items-center justify-center">
                    {Icon && (
                      <Icon size={20} className="text-white" strokeWidth={1.5} />
                    )}
                  </div>
                  <p className="mt-10 font-serif-display text-2xl text-white leading-tight">
                    {d.title}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRACTITIONER PERSPECTIVE */}
      <section
        className="py-24 sm:py-32 border-t border-white/10"
        data-testid="about-practitioner"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 reveal">
            <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/55 mb-6">
              {a.practitionerEyebrow}
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-white leading-[1.1] tracking-tight">
              {a.practitionerTitle}
            </h2>
          </div>

          <div className="lg:col-span-8">
            <ol className="border-t border-white/10">
              {a.practitionerPoints.map((p, i) => (
                <li
                  key={i}
                  data-testid={`practitioner-${i}`}
                  className="grid grid-cols-12 gap-4 sm:gap-8 py-8 border-b border-white/10 reveal"
                >
                  <span className="col-span-2 sm:col-span-1 flex items-start pt-3">
                    <span
                      aria-hidden="true"
                      className="block w-2.5 h-2.5 bg-white/60 rotate-45"
                    />
                  </span>
                  <p className="col-span-10 sm:col-span-11 text-base sm:text-lg font-sans-modern font-light text-white/80 leading-relaxed">
                    {p}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section
        className="py-24 sm:py-28 border-t border-white/10 bg-[#5C0E45]/50"
        data-testid="about-credentials"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex items-center gap-3 mb-12">
            <GraduationCap size={18} className="text-white/55" strokeWidth={1.5} />
            <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/55">
              {a.credentialsEyebrow}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {a.credentials.map((c, i) => (
              <div
                key={c.school}
                data-testid={`credential-${i}`}
                className="bg-[#711457] p-8 min-h-[160px] flex flex-col justify-between"
              >
                <span
                  aria-hidden="true"
                  className="block w-2.5 h-2.5 bg-white/55 rotate-45"
                />
                <div>
                  <p className="font-serif-display text-2xl text-white leading-tight">
                    {c.school}
                  </p>
                  <p className="mt-2 text-sm font-sans-modern text-white/65 leading-snug">
                    {c.program}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION + CTA */}
      <section
        className="relative py-28 sm:py-36 border-t border-white/10 overflow-hidden"
        data-testid="about-cta"
      >
        <img
          src={site.images.case}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-[#711457]/75" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-8 text-center reveal">
          <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/55 mb-6">
            Begin a conversation
          </p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
            The right counsel, before the next decision.
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-sm sm:text-base font-sans-modern text-white/70 leading-relaxed">
            {a.locationLine}
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-bottom"
            className="mt-10 inline-flex items-center justify-center h-12 px-10 bg-white text-[#711457] font-sans-modern font-semibold text-sm tracking-wide hover:bg-white/90 transition-colors"
          >
            Request a consultation
            <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default About;
